import { Box, Typography } from "@mui/material";
import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, INK } from "../../../theme/tokens";
import { type DropdownItem, SystemDropdown } from "../../UIToolkit/Inputs/SystemDropDown";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HOLIDAY_FIRST_PARTS, HOLIDAY_LAST_PARTS, type HolidayFirstPart, type HolidayLastPart } from "./holidayApi";
import { FIRST_PART_LABEL_KEY, LAST_PART_LABEL_KEY, PART_DAY_PART } from "./holidayFormat";

type Props = {
	firstPart: HolidayFirstPart;
	lastPart: HolidayLastPart;
	onFirstPartChange: (part: HolidayFirstPart) => void;
	onLastPartChange: (part: HolidayLastPart) => void;
	disabled?: boolean;
};

/** First shift / Last shift of a holiday — both included. */
export const HolidayShiftFields: FC<Props> = ({ firstPart, lastPart, onFirstPartChange, onLastPartChange, disabled }) => {
	const { t } = useTranslation();
	const firstItems = useMemo<DropdownItem<HolidayFirstPart>[]>(
		() =>
			HOLIDAY_FIRST_PARTS.map((part) => ({
				value: part,
				label: t(FIRST_PART_LABEL_KEY[part]),
				icon: <HolidayDayPartLabel dayPart={PART_DAY_PART[part]} iconOnly />,
			})),
		[t],
	);
	const lastItems = useMemo<DropdownItem<HolidayLastPart>[]>(
		() =>
			HOLIDAY_LAST_PARTS.map((part) => ({
				value: part,
				label: t(LAST_PART_LABEL_KEY[part]),
				icon: <HolidayDayPartLabel dayPart={PART_DAY_PART[part]} iconOnly />,
			})),
		[t],
	);
	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
			<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
				<SystemDropdown<DropdownItem<HolidayFirstPart>>
					items={firstItems}
					selectedItem={firstPart}
					onChange={(part) => {
						if (part !== undefined) onFirstPartChange(part);
					}}
					label={t("holidays.dialog.firstShift")}
					fullWidth
					disabled={disabled}
					search={false}
					menuPlacement="bottom-start"
					data-testid="holiday-dialog-first-shift"
				/>
				<SystemDropdown<DropdownItem<HolidayLastPart>>
					items={lastItems}
					selectedItem={lastPart}
					onChange={(part) => {
						if (part !== undefined) onLastPartChange(part);
					}}
					label={t("holidays.dialog.lastShift")}
					fullWidth
					disabled={disabled}
					search={false}
					menuPlacement="bottom-start"
					data-testid="holiday-dialog-last-shift"
				/>
			</Box>
			<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, color: INK.MUTED }}>{t("holidays.dialog.bothIncluded")}</Typography>
		</Box>
	);
};
